/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
'use strict';

import { useState, useContext, useEffect } from 'react';
import { Alert, Button, Container, Form, ProgressBar, Row, Col, Modal } from 'react-bootstrap';
import { BeButton } from './BeButton';
import * as dio from '../data';
import { BeContext } from './BeContext';
import Card from 'react-bootstrap/Card';
import { MetadataContext } from './MetadataContext';
import SaveDialog from './SaveDialog';
import { StatusContext } from './StatusContext';
import { WsContext } from './WsContext';
import { formatBytes } from '../utils/formatBytes';

function ExportTools() {
    const [ pOutName, setPOutName ] = useState('');
    const [ tName, setTName ] = useState('');
    const [ tOutName, setTOutName ] = useState('');
    const [ includeProbeData, setIncludeProbeData ] = useState(false);
    const [ openSubmitDialog, setOpenSubmitDialog ] = useState(false);
    const [ submitNotes, setSubmitNotes ] = useState('');
    const [ submitFilename, setSubmitFilename ] = useState('');
    const [ submitCloud, setSubmitCloud ] = useState(true);
    const [ submitProgressId, setSubmitProgressId ] = useState<string | null>(null);
    const [ submitProgressNow, setSubmitProgressNow ] = useState<number>(0);
    const [ submitProgressLabel, setSubmitProgressLabel ] = useState<string>('');
    const [ submitBytesTransferred, setSubmitBytesTransferred ] = useState<number>(0);
    const [ submitTotalBytes, setSubmitTotalBytes ] = useState<number | null>(null);
    const [ isSubmitting, setIsSubmitting ] = useState<boolean>(false);
    const [ submitError, setSubmitError ] = useState<string | null>(null);
    const [ openPublishDialog, setOpenPublishDialog ] = useState(false);
    const [ isExportingGit, setIsExportingGit ] = useState(false);
    const [ isImportingGit, setIsImportingGit ] = useState(false);
    const beContext = useContext(BeContext);
    const metadataContext = useContext(MetadataContext);
    const statusContext = useContext(StatusContext);
    const wsContext = useContext(WsContext);

    function resolveUsername() {
        const raw = (metadataContext as any)?.user_dir || '';
        const trimmed = String(raw || '').trim();
        if (!trimmed || trimmed === 'unknown' || trimmed === 'loading') {
            return 'user';
        }
        return trimmed;
    }

    function buildStamp(now: Date) {
        const pad = (value: number) => String(value).padStart(2, '0');
        const mm = pad(now.getMonth() + 1);
        const dd = pad(now.getDate());
        const yy = pad(now.getFullYear() % 100);
        const hh = pad(now.getHours());
        const min = pad(now.getMinutes());
        return `${mm}${dd}${yy}_${hh}${min}`;
    }

    useEffect(() => {
        if (!openSubmitDialog) {
            return;
        }
        const now = new Date();
        const username = resolveUsername();
        setSubmitNotes(`submitted by ${username} ${now.toLocaleString()}`);
        setSubmitFilename(`${username}_${buildStamp(now)}.fbot`);
        setSubmitCloud(true);
        setSubmitProgressId(null);
        setSubmitProgressNow(0);
        setSubmitProgressLabel('');
        setSubmitBytesTransferred(0);
        setSubmitTotalBytes(null);
        setIsSubmitting(false);
        setSubmitError(null);
    }, [openSubmitDialog]);

    useEffect(() => {
        if (!wsContext.ws || !openSubmitDialog) {
            return;
        }
        const handler = (msg: any) => {
            if (!submitProgressId || msg?.progressId !== submitProgressId || msg?.op !== 'save') {
                return;
            }
            if (typeof msg?.now === 'number') {
                setSubmitProgressNow(Math.max(0, Math.min(100, msg.now)));
            }
            if (typeof msg?.bytesTransferred === 'number') {
                setSubmitBytesTransferred(Math.max(0, msg.bytesTransferred));
            }
            if (typeof msg?.totalBytes === 'number' && msg.totalBytes > 0) {
                setSubmitTotalBytes(msg.totalBytes);
            }
            setSubmitProgressLabel(msg?.label || 'Submitting...');
        };
        wsContext.ws.on('fileTransferProgress', handler);
        return () => {
            wsContext.ws.off('fileTransferProgress', handler);
        };
    }, [wsContext.ws, openSubmitDialog, submitProgressId]);

    async function handlePublish(fileName: string, cloud: boolean, metadataOverride?: { info: string; serial: string }) {
        try {
            await dio.publish(fileName, cloud, metadataOverride);
            statusContext.postInfo(`Published ${fileName} ${cloud ? 'to cloud' : 'locally'}`);
            setOpenPublishDialog(false);
        } catch (e: any) {
            statusContext.postError(`Publish failed: ${e.message}`);
        }
    }

    async function handleSubmit() {
        const trimmedName = submitFilename.trim();
        if (!trimmedName) {
            setSubmitError('Filename is required.');
            return;
        }
        const progressId = crypto.randomUUID();
        setSubmitProgressId(progressId);
        setSubmitProgressNow(0);
        setSubmitProgressLabel('Preparing submit...');
        setSubmitBytesTransferred(0);
        setSubmitTotalBytes(null);
        setIsSubmitting(true);
        setSubmitError(null);
        const serial = '0000';
        const notes = submitNotes.trim();
        try {
            const res = await dio.exportDiff(trimmedName, submitCloud, { info: notes, serial }, progressId);
            statusContext.postInfo(`Submitted ${res.filename} to ${submitCloud ? 'cloud' : 'local'} queue (${res.fingerprints} fps, ${res.categories} cats)`);
            setOpenSubmitDialog(false);
        } catch (e: any) {
            setSubmitError(e?.message || 'Submit failed');
        } finally {
            setIsSubmitting(false);
        }
    }

    async function handleExportGit() {
        setIsExportingGit(true);
        try {
            const outFile = await dio.exportGit();
            statusContext.postInfo(`Exported database dump to ${outFile}`);
        } catch (e: any) {
            statusContext.postError(`Export to Git failed: ${e.message || e}`);
        } finally {
            setIsExportingGit(false);
        }
    }

    async function handleImportGit() {
        setIsImportingGit(true);
        try {
            const inFile = await dio.importGit();
            statusContext.postInfo(`Imported database text dump from ${inFile}`);
        } catch (e: any) {
            statusContext.postError(`Import from Git failed: ${e.message || e}`);
        } finally {
            setIsImportingGit(false);
        }
    }

    return (
    <Container fluid>
        <Row className="mt-4 ms-2 me-2">
            <Col className="col-3">
                <Card border="primary">
                    <Card.Header as="h5">
                        Submit
                        <span className="float-end text-muted fw-normal">QA/CS</span>
                    </Card.Header>
                    <Card.Body>
                        <Card.Text>
                            Submit the database into the review queue to be processed for merging into the main database.
                        </Card.Text>
                        <Button variant="primary" onClick={() => setOpenSubmitDialog(true)}>
                            Submit
                        </Button>
                    </Card.Body>
                </Card>
            </Col>
            <Col className="col-3">
                <Card border="primary">
                    <Card.Header as="h5">
                        Publish
                        <span className="float-end text-muted fw-normal">Engineering</span>
                    </Card.Header>
                    <Card.Body>
                        <Card.Text>
                            Publish a trimmed version of the database to the CI build chain for the dbfw product.
                        </Card.Text>
                        <Button variant="primary" onClick={() => setOpenPublishDialog(true)}>
                            Publish
                        </Button>
                    </Card.Body>
                </Card>
            </Col>
            <Col className="col-3">
                <Card border="primary">
                    <Card.Header as="h5">
                        ThreatStop
                        <span className="float-end text-muted fw-normal">Engineering</span>
                    </Card.Header>
                    <Card.Body>
                        <Card.Text>
                            This publishes fingerprints in the working set to a 
                            Threatstop format JSON file. This contains a flattened set of 
                            all DNS related matches for all fingerprints in the working set.
                        </Card.Text>
                        <input className="mb-4" type="text" placeholder="Filename" value={tName} onChange={(e) => setTName(e.target.value)} />
                        <BeButton label={`Export ${beContext.count()} fingerprints`}
                                   doit={async () => {
                            setPOutName('');
                            const fn = await dio.exportJval(tName);
                            setTOutName(fn);
                        }}/>
                        { tOutName && <div className="mt-4">Wrote {beContext.count()} fingerprints to <span className="font-monospace">{tOutName}</span></div> }
                        <Card.Text>
                            This creates the TS extract sample in the working set (any
                            existing working set is replaced). The sample is created
                            by sampling from the working curl probes then adding a group of
                            fingerprints that always should be there (identified by a tag).

                            This is then pushed to the threatstop sharing S3 bucket.
                        </Card.Text>
                        <input className="mb-4" type="text" placeholder="Filename" value={tName} onChange={(e) => setTName(e.target.value)} />
                        <BeButton label={`Publish TS extract`}
                                   doit={async () => {
                            const fn = await dio.publishTsExtract();
                        }}/>
                    </Card.Body>
                </Card>
            </Col>
            <Col className="col-3">
                <Card border="primary">
                    <Card.Header as="h5">
                        Git
                        <span className="float-end text-muted fw-normal">Engineering</span>
                    </Card.Header>
                    <Card.Body>
                        <Card.Text>
                            Import/Export the full active database to/from the git source repo as a binary database for version control.
                            <p/>
                            This does not include the local settings/work database (local.db).
                            This always reads/writes <span className="font-monospace">data/prod.db</span>.
                            <p/>
                            This doesn't do anything with the git commits or history - it's just a way to get the full database in and out of the git repo as a cleaned up file.
                        </Card.Text>
                        <div className="d-flex gap-5">
                            <Button variant="primary" onClick={handleImportGit} disabled={isExportingGit || isImportingGit}>
                                {isImportingGit ? 'Importing...' : 'Import'}
                            </Button>
                            <Button variant="primary" onClick={handleExportGit} disabled={isExportingGit || isImportingGit}>
                                {isExportingGit ? 'Exporting...' : 'Export'}
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
            </Col>
        </Row>

        <Modal show={openSubmitDialog} onHide={() => setOpenSubmitDialog(false)} centered>
            <Modal.Header closeButton>
                <Modal.Title>Submit</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Group className="mb-3" controlId="submitOrigin">
                    <Form.Label>Destination</Form.Label>
                    <div className="btn-group w-100" role="group">
                        <Button
                            variant={submitCloud ? 'primary' : 'outline-primary'}
                            onClick={() => setSubmitCloud(true)}
                            disabled={isSubmitting}
                        >
                            Cloud
                        </Button>
                        <Button
                            variant={!submitCloud ? 'primary' : 'outline-primary'}
                            onClick={() => setSubmitCloud(false)}
                            disabled={isSubmitting}
                        >
                            Local
                        </Button>
                    </div>
                </Form.Group>
                <Form.Group className="mb-3" controlId="submitFilename">
                    <Form.Label>Filename</Form.Label>
                    <Form.Control
                        type="text"
                        value={submitFilename}
                        onChange={(e) => setSubmitFilename(e.target.value)}
                    />
                    <Form.Text className="text-muted">
                        Saved to queue as <span className="font-monospace">library/queue/{submitFilename || '...'}</span>
                    </Form.Text>
                </Form.Group>
                <Form.Group className="mb-3" controlId="submitNotes">
                    <Form.Label>Metadata Notes</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={4}
                        value={submitNotes}
                        onChange={(e) => setSubmitNotes(e.target.value)}
                    />
                </Form.Group>
                <Form.Text className="text-muted d-block mb-3">
                    Serial is set to <span className="font-monospace">0000</span> for submissions.
                </Form.Text>

                {submitProgressId && (
                    <div className="mb-3">
                        <ProgressBar now={submitProgressNow} label={`${submitProgressNow.toFixed(0)}%`} />
                        <div className="mt-2 text-muted">
                            {submitProgressLabel || 'Submitting...'}
                            {submitTotalBytes ? (
                                <span className="ms-2">
                                    {formatBytes(submitBytesTransferred)} / {formatBytes(submitTotalBytes)}
                                </span>
                            ) : null}
                        </div>
                    </div>
                )}

                {submitError && (
                    <Alert variant="danger">{submitError}</Alert>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={() => setOpenSubmitDialog(false)} disabled={isSubmitting}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
                    Submit
                </Button>
            </Modal.Footer>
        </Modal>

        <SaveDialog
            isOpen={openPublishDialog}
            title="Publish"
            primaryLabel="Publish"
            filenameTemplate="ci3_1_{serial}_layer1.fbot"
            onSave={async (filename: string, cloud: boolean, metadataOverride?: { info: string; serial: string }, progressId?: string) => {
                await handlePublish(filename, cloud, metadataOverride);
            }}
            onCancel={() => setOpenPublishDialog(false)}
            cloud={true}
        />
    </Container>
    );
}

export default ExportTools;